import React, { useState } from 'react';
import styled from 'styled-components';
import { Check } from 'lucide-react';
import { theme } from '../styles/theme';

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 32px 20px 100px 20px;
  font-family: ${theme.typography.fontFamily};
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 24px 20px 100px 20px;
  }
`;

const Title = styled.h2`
  color: white;
  font-size: ${theme.typography.sizes['3xl']};
  margin: 0 0 1rem 0;
  text-align: center;
  font-weight: ${theme.typography.weights.bold};
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

const HostsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  max-width: 600px;
  width: 100%;
  justify-items: center;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
    max-width: calc(100% - 40px);
  }
`;

const HostCard = styled.div<{ $selected: boolean }>`
  background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: blur(24px);
  border: 2px solid ${props => props.$selected ? 'rgba(99, 102, 241, 0.5)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 16px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  position: relative;
  box-shadow: ${props => props.$selected ? '0 20px 40px rgba(99, 102, 241, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.2)'};
  
  &:hover {
    background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.4)' : 'rgba(255, 255, 255, 0.15)'};
    border-color: ${props => props.$selected ? 'rgba(99, 102, 241, 0.6)' : 'rgba(255, 255, 255, 0.3)'};
    transform: translateY(-4px);
    box-shadow: ${props => props.$selected ? '0 25px 50px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.4)' : '0 12px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)'};
  }
`;

const HostImage = styled.div<{ $image: string }>`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  margin: 0 auto 1rem;
  background: white;
  background-image: url(${props => props.$image});
  background-size: cover;
  background-position: center;
  border: 3px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const HostName = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 0.5rem;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.6);
  
  ${HostCard}[data-selected="true"] & {
    color: white;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.7);
    font-weight: 700;
  }
`;

const HostRole = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #fbbf24;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0.8rem;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.6);
  
  ${HostCard}[data-selected="true"] & {
    color: #fde047;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.7);
  }
`;

const HostDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  
  ${HostCard}[data-selected="true"] & {
    color: rgba(255, 255, 255, 0.95);
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.7);
  }
`;

const CheckIcon = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  background: rgba(147, 51, 234, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Button = styled.button`
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 40px);
  max-width: 600px;
  padding: 16px 24px;
  background: rgba(99, 102, 241, 0.95);
  backdrop-filter: blur(20px);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.4);
  border-radius: 10px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.4);
  
  &:hover:not(:disabled) {
    background: rgba(79, 70, 229, 0.95);
    border-color: rgba(79, 70, 229, 0.6);
    transform: translateX(-50%) translateY(-2px);
    box-shadow: 0 12px 40px rgba(99, 102, 241, 0.5);
  }
  
  &:disabled {
    background: rgba(209, 213, 219, 0.8);
    color: rgba(107, 114, 128, 0.8);
    border-color: rgba(209, 213, 219, 0.3);
    cursor: not-allowed;
    transform: translateX(-50%);
    opacity: 0.6;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    position: fixed;
    bottom: 20px;
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    width: calc(100% - 40px);
    max-width: 300px;
    padding: 12px 20px;
    font-size: 16px;
  }
`;

const hosts = [
  {
    id: 'sal',
    name: 'Sal',
    image: '/img/sal.webp',
    role: 'Color Commentator',
    description: "He doesn't hold back and will absolutely tell you how bad or good your lineup is."
  },
  {
    id: 'lisa',
    name: 'Lisa',
    image: '/img/lisa.webp',
    role: 'Matchup Expert',
    description: "She knows what players do well in what scenarios and matchups to look out for."
  },
  {
    id: 'tony',
    name: 'Tony',
    image: '/img/tony.webp',
    role: 'Analyst',
    description: "He will give you the facts on your players with a number-based approach."
  }
];

interface Props {
  onSelect: (hostId: string) => void;
}

export const HostSelector: React.FC<Props> = ({ onSelect }) => {
  const [selectedHost, setSelectedHost] = useState<string | null>(null);

  const handleNext = () => {
    if (selectedHost) {
      onSelect(selectedHost);
    }
  };

  return (
    <Container>
      <Title>🎙️ Pick your AI Football Expert</Title>
      
      <HostsGrid>
        {hosts.map(host => (
          <HostCard 
            key={host.id}
            $selected={host.id === selectedHost}
            data-selected={host.id === selectedHost}
            onClick={() => setSelectedHost(host.id)}
          >
            <HostImage $image={host.image} />
            <HostName>{host.name}</HostName>
            <HostRole>{host.role}</HostRole>
            <HostDescription>{host.description}</HostDescription>
            {host.id === selectedHost && (
              <CheckIcon>
                <Check size={18} color="white" />
              </CheckIcon>
            )}
          </HostCard>
        ))}
      </HostsGrid>
      
      <Button 
        onClick={handleNext}
        disabled={!selectedHost}
      >
        Select Host
      </Button>
    </Container>
  );
};
